import api from "./api";
import { Employee } from "../types/employee";
import type { EmployeeEditData } from "../types/employeeEditForm";

interface ApiEmployee {
  id: number | string;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  calling_code?: string | null;
  status?: string | null;
  role?: string | null;
  group?: string | null;
  employee_group?: string | null;
  image?: string | null;
  user_image_url?: string | null;
  address?: string | null;
  country?: string | null;
  formatted_mobile?: string | null;
  /** Legacy string list or `{ id, name }[]` from API */
  services?: unknown;
  locations?: Array<{ id?: number | string; name?: string } | string>;
  role_options?: Array<{ id: number; name: string; selected?: boolean }> | null;
  action?: {
    edit?: string;
    login_as_employee?: number;
    view_timesheet?: number;
    delete?: number;
  } | [];
  regular_schedules?: any[];
  schedule_overrides?: any[];
  pin_protected?: boolean;
  has_pin?: boolean;
  [key: string]: unknown;
}

interface ApiEmployeesResponse {
  success: boolean;
  data: ApiEmployee[];
  message?: string;
}

/** Employees API often returns `services: [{ id, name }, ...]` for assigned catalogue rows. */
export function extractEmployeeServiceIdsFromApi(services: unknown): string[] {
  if (!Array.isArray(services)) return [];
  const out: string[] = [];
  for (const item of services) {
    if (item == null || item === "") continue;
    if (typeof item === "object") {
      const o = item as Record<string, unknown>;
      const pivot = o.pivot && typeof o.pivot === "object" ? (o.pivot as Record<string, unknown>) : null;
      const idRaw =
        o.business_service_id ??
        pivot?.business_service_id ??
        o.id ??
        o.service_id ??
        pivot?.service_id;
      if (idRaw == null || idRaw === "") continue;
      const n = Number(idRaw);
      const s = Number.isFinite(n) && !Number.isNaN(n) ? String(n) : String(idRaw).trim();
      if (s && !out.some((x) => x === s || (Number(x) === Number(s) && Number.isFinite(Number(s))))) out.push(s);
      continue;
    }
    if (typeof item === "number" && Number.isFinite(item)) {
      const s = String(item);
      if (!out.includes(s)) out.push(s);
    } else if (typeof item === "string" && item.trim() !== "") {
      const s = item.trim();
      if (!out.includes(s)) out.push(s);
    }
  }
  return out;
}

const mapApiEmployee = (item: ApiEmployee): Employee => {
  const fullName =
    item.name ||
    [item.first_name, item.last_name].filter(Boolean).join(" ").trim();

  const mobile = item.mobile ?? item.phone ?? null;
  const image = item.user_image_url ?? item.image ?? "";
  const parsedId =
    typeof item.id === "number" ? item.id : Number.parseInt(String(item.id), 10);

  return {
    id: Number.isNaN(parsedId) ? 0 : parsedId,
    name: fullName || "Unnamed",
    email: item.email ?? "",
    phone: item.phone ?? undefined,
    mobile,
    calling_code: item.calling_code ?? null,
    status: item.status ?? null,
    role: (item as any).role_name ?? item.role ?? "",
    group: item.group ?? item.employee_group ?? null,
    services: extractEmployeeServiceIdsFromApi(item.services),
    locations: Array.isArray(item.locations) ? item.locations : [],
    image,
    user_image_url: image || null,
    formatted_mobile: item.formatted_mobile ?? null,
    address: item.address ?? undefined,
    country: item.country ?? undefined,
    role_options: Array.isArray(item.role_options)
      ? item.role_options.map((option) => ({
        id: option.id,
        name: option.name,
        selected: Boolean(option.selected),
      }))
      : null,
    action: item.action ?? [],
    regular_schedules: item.regular_schedules ?? [],
    schedule_overrides: item.schedule_overrides ?? [],
    pin_protected: Boolean(item.pin_protected ?? item.has_pin),
  };
};

export const getEmployees = async (): Promise<Employee[]> => {
  const response = await api.get<any>("/admin/employees");
  const payload = response.data;

  console.log("Employees API response:", payload);

  // Handle different response structures
  let employeesArray: ApiEmployee[] = [];

  if (payload?.success && Array.isArray(payload.data)) {
    employeesArray = payload.data;
  } else if (Array.isArray(payload?.employees)) {
    employeesArray = payload.employees;
  } else if (Array.isArray(payload?.data?.employees)) {
    employeesArray = payload.data.employees;
  } else if (Array.isArray(payload)) {
    employeesArray = payload;
  } else if (payload?.error) {
    // e.g. Unauthorized — let caller handle employee self-fallback
    const err: any = new Error(payload.error);
    err.response = { status: 403, data: payload };
    throw err;
  } else {
    console.warn("Employees API response structure unexpected:", payload);
    return [];
  }

  const mapped = employeesArray.map(mapApiEmployee);
  console.log("Mapped employees:", mapped);
  return mapped;
};

export interface EmployeeDetailsResponse {
  employee: Employee;
  statistics: {
    completed_bookings: number;
    approved_bookings: number;
    pending_bookings: number;
    canceled_bookings: number;
    in_progress_bookings: number;
    total_earning: number;
  };
  sales_data: {
    labels: string[];
    data: number[];
  };
}

export const getEmployeeById = async (id: string | number): Promise<Employee | null> => {
  try {
    // Attempt 1: Fetch direct resource
    const response = await api.get<any>(`/admin/employees/${id}`);
    const payload = response.data;

    if (payload) {
      // 1. { success: true, data: { ...employee } }
      if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
        if (payload.data.data && !Array.isArray(payload.data.data)) {
          return mapApiEmployee(payload.data.data);
        }
        return mapApiEmployee(payload.data);
      }

      // 2. { employee: { ... } } - Matches user payload
      if (payload.employee && typeof payload.employee === 'object') {
        return mapApiEmployee(payload.employee);
      }

      // 3. Direct object
      if (payload.id || payload.name) {
        return mapApiEmployee(payload);
      }
    }

    // Attempt 2: Fallback to list search if direct fetch returns no useful data
    const allEmployees = await getEmployees();
    return allEmployees.find(e => String(e.id) === String(id)) || null;

  } catch (error: any) {
    console.warn(`Direct fetch for employee ${id} failed, trying list fallback...`, error.message);

    // Attempt 3: Fallback to list search on exception (e.g., 404)
    try {
      const allEmployees = await getEmployees();
      return allEmployees.find(e => String(e.id) === String(id)) || null;
    } catch (fallbackError) {
      return null;
    }
  }
};

export const getEmployeeFullDetails = async (id: string | number): Promise<EmployeeDetailsResponse | null> => {
  try {
    const response = await api.get<any>(`/admin/employees/${id}`);
    const payload = response.data;

    if (payload && payload.employee) {
      return {
        employee: mapApiEmployee(payload.employee),
        statistics: payload.statistics || {
          completed_bookings: 0,
          approved_bookings: 0,
          pending_bookings: 0,
          canceled_bookings: 0,
          in_progress_bookings: 0,
          total_earning: 0,
        },
        sales_data: payload.sales_data || {
          labels: [],
          data: [],
        },
      };
    }

    // Fallback: If payload structure is different, try to get at least the employee
    const employee = await getEmployeeById(id);
    if (employee) {
      return {
        employee,
        statistics: {
          completed_bookings: 0,
          approved_bookings: 0,
          pending_bookings: 0,
          canceled_bookings: 0,
          in_progress_bookings: 0,
          total_earning: 0,
        },
        sales_data: {
          labels: [],
          data: [],
        },
      };
    }

    return null;
  } catch (error: any) {
    console.error(`Error fetching full employee details ${id}:`, error);
    return null;
  }
};

export interface CheckAvailabilityPayload {
  date: string;
  time: string;
  cart_services_data: (number | string)[];
  employee_id?: number | string;
}

export interface CheckAvailabilityResponse {
  status: "success" | "error";
  available: boolean;
  available_times?: string[]; // The API might return this
  message?: string;
}

export const checkEmployeeAvailability = async (payload: CheckAvailabilityPayload): Promise<CheckAvailabilityResponse> => {
  const response = await api.post<CheckAvailabilityResponse>("/admin/employees/check-availability", payload);
  return response.data;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
  return null;
}

function dedupeNums(nums: number[]): number[] {
  const out: number[] = [];
  for (const n of nums) {
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

/** Collect numeric ids from arrays / single values / pivot rows (Laravel employee edit payloads). */
function toNumArray(v: unknown): number[] {
  if (v == null || v === "") return [];
  if (typeof v === "number" && Number.isFinite(v)) return [v];
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) && !Number.isNaN(n) ? [n] : [];
  }
  if (!Array.isArray(v)) return [];
  const out: number[] = [];
  for (const x of v) {
    if (typeof x === "number" && Number.isFinite(x)) out.push(x);
    else if (typeof x === "string") {
      const n = Number(x);
      if (!Number.isNaN(n)) out.push(n);
    } else if (x && typeof x === "object") {
      const o = x as Record<string, unknown>;
      const pivot = asRecord(o.pivot);
      const id =
        o.business_service_id ??
        pivot?.business_service_id ??
        o.id ??
        o.service_id ??
        pivot?.service_id;
      if (id != null && id !== "") {
        const n = Number(id);
        if (!Number.isNaN(n)) out.push(n);
      }
    }
  }
  return dedupeNums(out);
}

function unwrapEmployeePayload(payload: unknown): Record<string, unknown> | null {
  if (payload == null || typeof payload !== "object") return null;
  let cur = payload as Record<string, unknown>;
  for (let depth = 0; depth < 3; depth++) {
    const data = cur.data;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      const next = data as Record<string, unknown>;
      if (next.employee != null || next.roles != null || next.business_services != null || next.selected_services != null) {
        cur = next;
        continue;
      }
      if (next.data && typeof next.data === "object" && !Array.isArray(next.data)) {
        cur = next.data as Record<string, unknown>;
        continue;
      }
    }
    break;
  }
  return cur;
}

function extractBusinessServices(inner: Record<string, unknown>): EmployeeEditData["business_services"] {
  const keys = ["business_services", "company_services", "catalog_services", "services"];
  for (const k of keys) {
    const v = inner[k];
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === "object") {
      return v as EmployeeEditData["business_services"];
    }
  }
  return [];
}

/**
 * Normalizes GET /admin/employees/:id (and similar) responses for the employee drawer.
 */
export function parseEmployeeEditFormPayload(payload: unknown): EmployeeEditData | null {
  const inner = unwrapEmployeePayload(payload);
  if (!inner) return null;

  const employeeRaw =
    inner.employee ??
    inner.user ??
    (typeof inner.name === "string" && (inner.id != null || inner.email != null) ? inner : null);
  if (!employeeRaw || typeof employeeRaw !== "object") return null;
  const employee = employeeRaw as Record<string, unknown>;

  let selected_services = toNumArray(
    inner.selected_services ??
      inner.service_ids ??
      employee.selected_services ??
      employee.service_ids ??
      employee.service_id
  );

  if (selected_services.length === 0) {
    selected_services = toNumArray(employee.employee_services ?? employee.business_services);
  }

  // Assigned services often repeated as `employee.services: [{ id, name }, ...]` on list/detail API
  selected_services = dedupeNums([...selected_services, ...toNumArray(employee.services)]);

  const business_services = extractBusinessServices(inner);

  if (selected_services.length === 0 && business_services.length > 0) {
    const fromCatalog = business_services
      .filter((s) => {
        const o = s as Record<string, unknown>;
        const pivot = asRecord(o.pivot);
        return (
          o.selected === true ||
          o.is_selected === true ||
          o.attached === true ||
          (pivot != null && (pivot.business_service_id != null || pivot.service_id != null))
        );
      })
      .map((s) => Number((s as { id?: number }).id))
      .filter((n) => !Number.isNaN(n));
    if (fromCatalog.length > 0) selected_services = dedupeNums(fromCatalog);
  }

  const selected_locations = toNumArray(
    inner.selected_locations ?? inner.location_ids ?? employee.location_ids ?? employee.selected_locations
  );

  const groups = (Array.isArray(inner.groups) ? inner.groups : []) as EmployeeEditData["groups"];
  const roles = (Array.isArray(inner.roles) ? inner.roles : []) as EmployeeEditData["roles"];
  const locations = (Array.isArray(inner.locations) ? inner.locations : []) as EmployeeEditData["locations"];
  const shifts = (Array.isArray(inner.shifts) ? inner.shifts : []) as EmployeeEditData["shifts"];
  const employee_details =
    (asRecord(inner.employee_details) ??
      asRecord(inner.details) ??
      employee) as Record<string, unknown>;

  return {
    employee,
    groups,
    roles,
    selected_services,
    selected_locations,
    business_services,
    locations,
    shifts,
    employee_details,
  };
}

export async function fetchEmployeeEditForm(id: string | number): Promise<EmployeeEditData | null> {
  try {
    const response = await api.get<unknown>(`/admin/employees/${id}`);
    return parseEmployeeEditFormPayload(response.data);
  } catch {
    return null;
  }
}
